import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Country, State, City } from 'country-state-city';
import LoadingSpinner from './LoadingSpinner';
import './DynamicFormRenderer.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const DynamicFormRenderer = ({
    entityId,
    companyEntityId,
    onSave,
    onCancel,
    onError,
    onValidationError,
    initialAnswers = {}
}) => {
    const [formConfig, setFormConfig] = useState(null);
    const [answers, setAnswers] = useState(initialAnswers);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [hasAccessError, setHasAccessError] = useState(false);
    const [openGeoDropdowns, setOpenGeoDropdowns] = useState({});
    const geoDropdownRefs = useRef({});

    const loadFormConfig = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/dynamic-forms/entity/${entityId}`);
            if (!response.ok) throw new Error('Failed to load form configuration');
            const data = await response.json();
            setFormConfig(data);
        } catch (err) {
            setError(`Failed to load form: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [entityId]);

    const loadExistingAnswers = useCallback(async () => {
        if (!companyEntityId) return;

        try {
            const token = localStorage.getItem('f4c_web_token');
            const response = await fetch(`${API_BASE_URL}/dynamic-forms/company-entity/${companyEntityId}/answers`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 404) {
                const errorData = await response.json();
                const errorMessage = errorData.detail || 'Company entity not found';
                setHasAccessError(true);
                setLoading(false);
                if (onError) {
                    onError(errorMessage);
                }
                return;
            }

            if (response.status === 403) {
                const errorData = await response.json();
                const errorMessage = errorData.detail || 'Access denied. You do not have permission to access this company profile.';
                setHasAccessError(true);
                setLoading(false);
                if (onError) {
                    onError(errorMessage);
                }
                return;
            }

            if (response.status === 401) {
                const errorMessage = 'Authentication required. Please log in again.';
                setHasAccessError(true);
                setLoading(false);
                if (onError) {
                    onError(errorMessage);
                }
                return;
            }

            if (response.ok) {
                const data = await response.json();
                const rawAnswers = data.answers || {};

                // Process answers to extract the appropriate value based on the answer structure
                const processedAnswers = {};
                Object.entries(rawAnswers).forEach(([questionId, answerObj]) => {
                    if (answerObj && typeof answerObj === 'object') {
                        // Extract the appropriate value based on what's available
                        if (answerObj.file_url) {
                            processedAnswers[questionId] = answerObj.file_url;
                        } else if (answerObj.answer_text !== null) {
                            processedAnswers[questionId] = answerObj.answer_text;
                        } else if (answerObj.answer_json !== null) {
                            processedAnswers[questionId] = answerObj.answer_json;
                        } else {
                            processedAnswers[questionId] = '';
                        }
                    } else {
                        processedAnswers[questionId] = answerObj || '';
                    }
                });

                console.log('Raw answers from API:', rawAnswers);
                console.log('Processed answers for frontend:', processedAnswers);
                setAnswers(processedAnswers);
            }
        } catch (err) {
            console.error('Failed to load existing answers:', err);
            setError(`Failed to load answers: ${err.message}`);
        }
    }, [companyEntityId, onError]);

    useEffect(() => {
        loadFormConfig();
        loadExistingAnswers();
    }, [loadFormConfig, loadExistingAnswers]);

    // Force re-render when answers change to update subcategories
    useEffect(() => {
        // This will trigger a re-render when answers change
    }, [answers]);

    // Close geo dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(geoDropdownRefs.current).forEach((questionId) => {
                const ref = geoDropdownRefs.current[questionId];
                if (ref && !ref.contains(event.target)) {
                    setOpenGeoDropdowns(prev => ({
                        ...prev,
                        [questionId]: false
                    }));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const updateAnswer = (questionId, value) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const handleMultiSelect = (questionId, optionValue, checked) => {
        const currentAnswers = answers[questionId] || [];
        let newAnswers;

        if (checked) {
            newAnswers = [...currentAnswers, optionValue];
        } else {
            // Remove the option
            newAnswers = currentAnswers.filter(val => val !== optionValue);

            // If unchecking a main option, also remove all its suboptions
            if (formConfig) {
                // Find the question in formConfig
                const question = formConfig.sections
                    .flatMap(section => section.questions)
                    .find(q => q.id === questionId);

                if (question && question.options) {
                    // Find the option being unchecked
                    const uncheckedOption = question.options.find(opt => opt.option_value === optionValue);

                    // If this option has suboptions, remove them all
                    if (uncheckedOption && uncheckedOption.has_suboptions && uncheckedOption.suboptions) {
                        const suboptionValues = uncheckedOption.suboptions.map(sub => sub.suboption_value);
                        newAnswers = newAnswers.filter(val => !suboptionValues.includes(val));
                    }
                }
            }
        }

        updateAnswer(questionId, newAnswers);
    };


    const handleFileUpload = async (questionId, file, validationRules = {}) => {
        if (!file) return;

        // Validate file type if specified
        if (validationRules.accept) {
            const acceptedTypes = validationRules.accept.split(',').map(t => t.trim().toLowerCase());
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            const fileMimeType = file.type.toLowerCase();

            const isValidType = acceptedTypes.some(type =>
                type === fileExtension || type === fileMimeType
            );

            if (!isValidType) {
                const allowedFormats = (validationRules.fileTypes || []).join(', ');
                setError(`Invalid file type. Accepted formats: ${allowedFormats || 'See requirements'}`);
                return;
            }
        }

        // Validate file size if specified
        if (validationRules.maxFileSize && file.size > validationRules.maxFileSize) {
            const maxSizeMB = validationRules.maxFileSizeMB || Math.round(validationRules.maxFileSize / 1024 / 1024);
            setError(`File size exceeds ${maxSizeMB}MB limit. Please upload a smaller file.`);
            return;
        }

        try {
            const token = localStorage.getItem('f4c_web_token');
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(
                `${API_BASE_URL}/dynamic-forms/company-entity/${companyEntityId}/upload-file?question_id=${questionId}`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                }
            );

            if (response.status === 403) {
                throw new Error('Access denied. You do not have permission to upload files for this profile.');
            }

            if (response.status === 401) {
                throw new Error('Authentication required. Please log in again.');
            }

            if (!response.ok) throw new Error('File upload failed');

            const result = await response.json();
            const s3Key = result.file_url || result.url || result;
            console.log('File upload result:', result);
            console.log('S3 Key extracted:', s3Key);

            // Generate presigned URL immediately after upload
            const presignedResponse = await fetch(
                `${API_BASE_URL}/dynamic-forms/company-entity/${companyEntityId}/file-url/${encodeURIComponent(s3Key)}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (presignedResponse.ok) {
                const presignedData = await presignedResponse.json();
                console.log('Presigned URL generated:', presignedData.presigned_url);
                updateAnswer(questionId, presignedData.presigned_url);
            } else {
                console.log('Presigned URL generation failed, using S3 key as fallback');
                // Fallback to S3 key if presigned URL generation fails
                updateAnswer(questionId, s3Key);
            }
        } catch (err) {
            setError(`File upload failed: ${err.message}`);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Prevent submission if there's an access error
        if (hasAccessError) {
            return;
        }

        // Note: Required field validation removed to allow saving partial progress
        // The backend will automatically set is_onboarded based on completion status

        setSaving(true);
        setError(null);

        try {
            // Convert answers to submission format
            const answerSubmissions = Object.entries(answers).map(([questionId, value]) => {
                const submission = {
                    question_id: parseInt(questionId),
                    answer_text: null,
                    answer_json: null,
                    file_url: null
                };

                // Find the question to determine its type
                const question = formConfig?.sections
                    ?.flatMap(section => section.questions || [])
                    ?.find(q => q.id === parseInt(questionId));

                if (typeof value === 'string') {
                    // Check if it's a presigned URL (contains AWS signature parameters)
                    if (value.includes('X-Amz-Algorithm') || value.includes('X-Amz-Signature')) {
                        // Extract S3 key from presigned URL
                        const s3Key = value.split('?')[0].split('/').slice(3).join('/'); // Remove domain and get path after bucket
                        submission.file_url = s3Key;
                    } else if (value.startsWith('dynamic-forms/')) {
                        // It's already an S3 key
                        submission.file_url = value;
                    } else if (question?.question_type === 'currency') {
                        // For currency, save as numeric value (remove commas and convert to number)
                        const numericValue = parseCurrency(value);
                        submission.answer_text = numericValue ? parseFloat(numericValue).toString() : '';
                    } else {
                        // Regular text answer
                        submission.answer_text = value;
                    }
                } else if (Array.isArray(value)) {
                    submission.answer_json = value;
                } else if (typeof value === 'object' && value !== null) {
                    submission.answer_json = value;
                }

                return submission;
            });

            console.log('Submitting answers:', answerSubmissions);

            const token = localStorage.getItem('f4c_web_token');
            const response = await fetch(
                `${API_BASE_URL}/dynamic-forms/company-entity/${companyEntityId}/answers`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(answerSubmissions)
                }
            );

            if (response.status === 404) {
                const errorData = await response.json();
                const errorMessage = errorData.detail || 'Company entity not found';
                setHasAccessError(true);
                if (onError) {
                    onError(errorMessage);
                }
                return;
            }

            if (response.status === 403) {
                const errorData = await response.json();
                const errorMessage = errorData.detail || 'Access denied. You do not have permission to save this profile.';
                setHasAccessError(true);
                if (onError) {
                    onError(errorMessage);
                }
                return;
            }

            if (response.status === 401) {
                const errorMessage = 'Authentication required. Please log in again.';
                setHasAccessError(true);
                if (onError) {
                    onError(errorMessage);
                }
                return;
            }

            if (!response.ok) throw new Error('Failed to save answers');

            onSave && onSave();
        } catch (err) {
            setError(`Save failed: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    // Format number as currency with commas (e.g., 1234567.89 -> "1,234,567.89")
    const formatCurrency = (value) => {
        if (!value && value !== 0) return '';
        const numValue = typeof value === 'string' ? value.replace(/[^0-9.]/g, '') : String(value);
        const parts = numValue.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return parts.length > 1 ? parts.slice(0, 2).join('.') : parts[0];
    };

    // Parse currency string to number (e.g., "$1,234,567.89" -> 1234567.89)
    const parseCurrency = (value) => {
        if (!value) return '';
        const cleaned = String(value).replace(/[^0-9.]/g, '');
        return cleaned;
    };

    // Handle currency input changes
    const handleCurrencyChange = (questionId, inputValue) => {
        // Remove all non-numeric characters except decimal point
        const cleaned = inputValue.replace(/[^0-9.]/g, '');

        // Ensure only one decimal point
        const parts = cleaned.split('.');
        const formatted = parts.length > 2
            ? parts[0] + '.' + parts.slice(1).join('')
            : cleaned;

        // Store the raw numeric value (without commas)
        updateAnswer(questionId, formatted);
    };

    const renderQuestion = (question) => {
        const { id, question_type, options = [], validation_rules } = question;
        const rawValue = answers[id];
        // Ensure currentValue is always a string for text-based inputs
        const currentValue = (question_type === 'multiselect' || question_type === 'checkbox')
            ? (rawValue || [])
            : String(rawValue || '');

        const commonProps = {
            value: currentValue,
            onChange: (e) => updateAnswer(id, e.target.value)
            // Note: removed 'required' to allow saving partial progress
        };

        switch (question_type) {
            case 'text':
                return (
                    <div className="question-group">
                        <input
                            {...commonProps}
                            type="text"
                            className="form-input"
                            placeholder={validation_rules?.placeholder || 'Enter your answer'}
                            maxLength={validation_rules?.max_length}
                            minLength={validation_rules?.min_length}
                        />
                        {validation_rules?.max_length && (
                            <div className={`character-count ${(currentValue || '').length > validation_rules.max_length * 0.9 ? 'warning' : ''} ${(currentValue || '').length > validation_rules.max_length ? 'error' : ''}`}>
                                {(currentValue || '').length}/{validation_rules.max_length} characters
                            </div>
                        )}
                    </div>
                );

            case 'textarea':
                const maxLength = validation_rules?.maxLength || validation_rules?.max_length;
                const minLength = validation_rules?.minLength || validation_rules?.min_length;
                return (
                    <div className="question-group">
                        <textarea
                            {...commonProps}
                            className="form-textarea"
                            rows={4}
                            placeholder={validation_rules?.placeholder || 'Enter your answer'}
                            maxLength={maxLength}
                            minLength={minLength}
                        />
                        {maxLength && (
                            <div className={`character-count ${(currentValue || '').length > maxLength * 0.9 ? 'warning' : ''} ${(currentValue || '').length > maxLength ? 'error' : ''}`}>
                                {(currentValue || '').length}/{maxLength} characters
                            </div>
                        )}
                    </div>
                );

            case 'number':
                return (
                    <div className="question-group">
                        <input
                            {...commonProps}
                            type="number"
                            className="form-input"
                            min={validation_rules?.min}
                            max={validation_rules?.max}
                            placeholder={validation_rules?.placeholder || 'Enter a number'}
                            onBlur={(e) => {
                                // Enforce min/max on blur
                                const value = parseFloat(e.target.value);
                                if (!isNaN(value)) {
                                    const min = validation_rules?.min;
                                    const max = validation_rules?.max;
                                    if (min !== undefined && value < min) {
                                        e.target.value = min;
                                        updateAnswer(question.id, min.toString());
                                    } else if (max !== undefined && value > max) {
                                        e.target.value = max;
                                        updateAnswer(question.id, max.toString());
                                    }
                                }
                            }}
                        />
                    </div>
                );

            case 'currency':
                return (
                    <div className="question-group">
                        <div className="currency-input-wrapper">
                            <span className="currency-symbol">$</span>
                            <input
                                type="text"
                                className="form-input currency-input"
                                value={formatCurrency(currentValue)}
                                onChange={(e) => handleCurrencyChange(id, e.target.value)}
                                placeholder={validation_rules?.placeholder || '0.00'}
                                inputMode="decimal"
                            />
                        </div>
                    </div>
                );

            case 'url':
                return (
                    <div className="question-group">
                        <input
                            {...commonProps}
                            type="url"
                            className="form-input"
                            placeholder={validation_rules?.placeholder || 'https://example.com'}
                        />
                    </div>
                );

            case 'select':
                return (
                    <div className="question-group">
                        <div className="select-wrapper">
                            <select
                                value={currentValue}
                                onChange={(e) => updateAnswer(id, e.target.value)}
                                className="form-select"
                            >
                                <option value="">Select an option</option>
                                {options.map((option) => (
                                    <option key={option.option_value} value={option.option_value}>
                                        {option.option_text}
                                    </option>
                                ))}
                            </select>
                            <i className="fas fa-chevron-down select-icon"></i>
                        </div>
                    </div>
                );

            case 'radio':
                return (
                    <div className="question-group">
                        <div className="radio-group">
                            {options.map((option) => (
                                <label key={option.option_value} className="radio-option">
                                    <input
                                        type="radio"
                                        name={`question-${id}`}
                                        value={option.option_value}
                                        checked={currentValue === option.option_value}
                                        onChange={() => updateAnswer(id, option.option_value)}
                                    />
                                    <span className="radio-text">{option.option_text}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                );

            case 'multiselect':
            case 'checkbox':
                return (
                    <div className="question-group">
                        <div className="checkbox-group">
                            {options.map((option) => (
                                <div key={option.option_value}>
                                    <label className="checkbox-option">
                                        <input
                                            type="checkbox"
                                            value={option.option_value}
                                            checked={(currentValue || []).includes(option.option_value)}
                                            onChange={(e) => handleMultiSelect(id, option.option_value, e.target.checked)}
                                        />
                                        <span className="checkbox-text">{option.option_text}</span>
                                    </label>

                                    {/* Render suboptions if this option has them and is selected */}
                                    {option.has_suboptions && option.suboptions && option.suboptions.length > 0 &&
                                        (currentValue || []).includes(option.option_value) && (
                                            <div className="subcategory-group">
                                                <div className="subcategory-label">Select specific subcategories:</div>
                                                {option.suboptions.map((suboption) => (
                                                    <label key={suboption.suboption_value} className="checkbox-option subcategory-option">
                                                        <input
                                                            type="checkbox"
                                                            value={suboption.suboption_value}
                                                            checked={(currentValue || []).includes(suboption.suboption_value)}
                                                            onChange={(e) => handleMultiSelect(id, suboption.suboption_value, e.target.checked)}
                                                        />
                                                        <span className="checkbox-text subcategory-text">{suboption.suboption_text}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'file':
                return (
                    <div className="question-group">
                        {/* Display validation requirements if they exist */}
                        {validation_rules && (validation_rules.maxFileSizeMB || validation_rules.fileTypes || validation_rules.description) && (
                            <div className="file-requirements" style={{
                                padding: '12px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                marginBottom: '12px',
                                fontSize: '14px',
                                color: '#666',
                                textAlign: 'left'
                            }}>
                                <strong style={{ color: '#333', display: 'block', marginBottom: '6px', textAlign: 'left' }}>File Requirements:</strong>
                                <div style={{ lineHeight: '1.6', textAlign: 'left' }}>
                                    {validation_rules.maxFileSizeMB && (
                                        <span>Maximum file size: {validation_rules.maxFileSizeMB}MB. </span>
                                    )}
                                    {validation_rules.fileTypes && (
                                        <span>Accepted format{validation_rules.fileTypes.length > 1 ? 's' : ''}: {validation_rules.fileTypes.join(', ')}{validation_rules.fileTypes.length === 1 ? ' only' : ''}.</span>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="file-upload-wrapper">
                            <input
                                type="file"
                                className="file-upload-input"
                                onChange={(e) => handleFileUpload(id, e.target.files?.[0], validation_rules)}
                                accept={validation_rules?.accept || '*'}
                            />
                            {currentValue && (
                                <div className="file-upload-success">
                                    {typeof currentValue === 'string' ? (
                                        <a
                                            href={currentValue}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="file-link"
                                            style={{
                                                color: '#1976d2',
                                                textDecoration: 'underline',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => console.log('File link clicked:', currentValue)}
                                        >
                                            File uploaded
                                        </a>
                                    ) : (
                                        'Unknown file'
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'file_image':
                return (
                    <div className="question-group">
                        {/* Display validation requirements if they exist */}
                        {validation_rules && (validation_rules.maxFileSizeMB || validation_rules.recommendedWidth || validation_rules.fileTypes) && (
                            <div className="file-requirements" style={{
                                padding: '12px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                marginBottom: '12px',
                                fontSize: '14px',
                                color: '#666',
                                textAlign: 'left'
                            }}>
                                <strong style={{ color: '#333', display: 'block', marginBottom: '6px', textAlign: 'left' }}>File Requirements:</strong>
                                <div style={{ lineHeight: '1.6', textAlign: 'left' }}>
                                    {validation_rules.fileTypes && (
                                        <span>Accepted format{validation_rules.fileTypes.length > 1 ? 's' : ''}: {validation_rules.fileTypes.join(', ')}. </span>
                                    )}
                                    {validation_rules.maxFileSizeMB && (
                                        <span>Maximum file size: {validation_rules.maxFileSizeMB}MB. </span>
                                    )}
                                    {validation_rules.recommendedWidth && validation_rules.recommendedHeight && (
                                        <span>Recommended dimensions: {validation_rules.recommendedWidth}px × {validation_rules.recommendedHeight}px.</span>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="file-upload-wrapper">
                            <input
                                type="file"
                                className="file-upload-input"
                                onChange={(e) => handleFileUpload(id, e.target.files?.[0], validation_rules)}
                                accept={validation_rules?.accept || ".png,.jpg,.jpeg,image/png,image/jpeg"}
                            />
                            {currentValue && (
                                <div className="image-preview-container">
                                    {typeof currentValue === 'string' ? (
                                        <>
                                            <img
                                                src={currentValue}
                                                alt="Uploaded preview"
                                                className="image-preview"
                                                onError={(e) => {
                                                    console.error('Image failed to load:', currentValue);
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                            <a
                                                href={currentValue}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="image-link"
                                            >
                                                View full size
                                            </a>
                                        </>
                                    ) : (
                                        'Unknown file'
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'geographic_multiselect':
                const geoValue = typeof currentValue === 'string' && currentValue ? JSON.parse(currentValue) : (currentValue || {});
                const selectedCountries = Object.keys(geoValue);
                const allCountries = Country.getAllCountries().sort((a, b) => a.name.localeCompare(b.name));
                const isGeoDropdownOpen = openGeoDropdowns[id] || false;

                const handleCountryToggle = (countryCode) => {
                    const newValue = { ...geoValue };
                    if (newValue[countryCode]) {
                        delete newValue[countryCode];
                    } else {
                        newValue[countryCode] = [];
                    }
                    updateAnswer(id, JSON.stringify(newValue));
                };

                const handleStateToggle = (countryCode, stateCode) => {
                    const newValue = { ...geoValue };
                    if (!newValue[countryCode]) {
                        newValue[countryCode] = [];
                    }
                    const stateIndex = newValue[countryCode].indexOf(stateCode);
                    if (stateIndex > -1) {
                        newValue[countryCode].splice(stateIndex, 1);
                    } else {
                        newValue[countryCode].push(stateCode);
                    }
                    updateAnswer(id, JSON.stringify(newValue));
                };

                const getCountryDisplayText = () => {
                    if (selectedCountries.length === 0) return '';
                    return selectedCountries.map(code => {
                        const country = allCountries.find(c => c.isoCode === code);
                        return country?.name || code;
                    }).join(', ');
                };

                return (
                    <div className="question-group">
                        <div className="geographic-selector">
                            {/* Country Dropdown */}
                            <div
                                className="custom-dropdown"
                                ref={(el) => {
                                    if (el) geoDropdownRefs.current[id] = el;
                                }}
                            >
                                <div
                                    className="dropdown-trigger"
                                    onClick={() => setOpenGeoDropdowns(prev => ({
                                        ...prev,
                                        [id]: !prev[id]
                                    }))}
                                >
                                    <span className="dropdown-text">
                                        {getCountryDisplayText() || 'Select countries (at least one required)'}
                                    </span>
                                    <span className={`dropdown-arrow ${isGeoDropdownOpen ? 'open' : ''}`}>▼</span>
                                </div>

                                {isGeoDropdownOpen && (
                                    <div className="dropdown-menu" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {allCountries.map((country) => (
                                            <label key={country.isoCode} className="dropdown-checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedCountries.includes(country.isoCode)}
                                                    onChange={() => handleCountryToggle(country.isoCode)}
                                                />
                                                <span>{country.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* State/Province Sections for Selected Countries */}
                            {selectedCountries.length > 0 && (
                                <div className="geo-states-container">
                                    {selectedCountries.map((countryCode) => {
                                        const country = allCountries.find(c => c.isoCode === countryCode);
                                        // Filter out US Minor Outlying Islands (UM codes) - tiny uninhabited territories
                                        const states = State.getStatesOfCountry(countryCode)
                                            .filter(state => countryCode !== 'US' || !state.isoCode.startsWith('UM'));

                                        return (
                                            <div key={countryCode} className="geo-section country-states">
                                                <div className="geo-label">
                                                    {country?.name} - Select States/Provinces (optional)
                                                </div>
                                                {states && states.length > 0 ? (
                                                    <div className="checkbox-group states-grid">
                                                        {states.map((state) => (
                                                            <label key={state.isoCode} className="checkbox-option">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={(geoValue[countryCode] || []).includes(state.isoCode)}
                                                                    onChange={() => handleStateToggle(countryCode, state.isoCode)}
                                                                />
                                                                <span className="checkbox-text">{state.name}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="no-states-message">
                                                        No states/provinces available for this country
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'geographic_singleselect':
                const singleGeoValue = typeof currentValue === 'string' && currentValue ? JSON.parse(currentValue) : (currentValue || {});
                const selectedCountry = singleGeoValue.country || '';
                const selectedState = singleGeoValue.state || '';
                const selectedCity = singleGeoValue.city || '';

                const allCountriesForSingle = Country.getAllCountries().sort((a, b) => a.name.localeCompare(b.name));
                const statesForCountry = selectedCountry ? State.getStatesOfCountry(selectedCountry) : [];
                const citiesForState = selectedCountry && selectedState ? City.getCitiesOfState(selectedCountry, selectedState) : [];

                const handleSingleCountryChange = (countryCode) => {
                    const newValue = countryCode ? { country: countryCode, state: '', city: '' } : {};
                    updateAnswer(id, JSON.stringify(newValue));
                };

                const handleSingleStateChange = (stateCode) => {
                    const newValue = { ...singleGeoValue, state: stateCode, city: '' };
                    updateAnswer(id, JSON.stringify(newValue));
                };

                const handleSingleCityChange = (cityName) => {
                    const newValue = { ...singleGeoValue, city: cityName };
                    updateAnswer(id, JSON.stringify(newValue));
                };

                return (
                    <div className="question-group">
                        <div className="geographic-selector">
                            {/* Country Dropdown */}
                            <div className="geo-section">
                                <select
                                    value={selectedCountry}
                                    onChange={(e) => handleSingleCountryChange(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="">Select a country</option>
                                    {allCountriesForSingle.map((country) => (
                                        <option key={country.isoCode} value={country.isoCode}>
                                            {country.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* State/Province Dropdown */}
                            {selectedCountry && (
                                <div className="geo-section">
                                    {statesForCountry && statesForCountry.length > 0 ? (
                                        <select
                                            value={selectedState}
                                            onChange={(e) => handleSingleStateChange(e.target.value)}
                                            className="form-select"
                                        >
                                            <option value="">Select a state/province</option>
                                            {statesForCountry.map((state) => (
                                                <option key={state.isoCode} value={state.isoCode}>
                                                    {state.name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className="no-states-message">No states/provinces available for this country</div>
                                    )}
                                </div>
                            )}

                            {/* City Dropdown */}
                            {selectedCountry && selectedState && (
                                <div className="geo-section">
                                    {citiesForState && citiesForState.length > 0 ? (
                                        <select
                                            value={selectedCity}
                                            onChange={(e) => handleSingleCityChange(e.target.value)}
                                            className="form-select"
                                        >
                                            <option value="">Select a city</option>
                                            {citiesForState.map((city) => (
                                                <option key={city.name} value={city.name}>
                                                    {city.name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className="no-states-message">No cities available for this state/province</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'geographic_states_countries_multiselect':
                const statesCountriesValue = typeof currentValue === 'string' && currentValue ? JSON.parse(currentValue) : (currentValue || []);
                // Filter out US Minor Outlying Islands (UM and UM-XX codes) - these are tiny uninhabited territories
                const usStates = State.getStatesOfCountry('US')
                    .filter(state => !state.isoCode.startsWith('UM'))
                    .sort((a, b) => a.name.localeCompare(b.name));
                const internationalCountries = Country.getAllCountries()
                    .filter(country => country.isoCode !== 'US')
                    .sort((a, b) => a.name.localeCompare(b.name));

                const isStateSelected = (stateCode) => {
                    return statesCountriesValue.includes(`US-${stateCode}`);
                };

                const isCountrySelected = (countryCode) => {
                    return statesCountriesValue.includes(`COUNTRY-${countryCode}`);
                };

                const handleStateToggleClick = (stateCode) => {
                    const stateId = `US-${stateCode}`;
                    const newValue = [...statesCountriesValue];
                    const index = newValue.indexOf(stateId);

                    if (index > -1) {
                        newValue.splice(index, 1);
                    } else {
                        newValue.push(stateId);
                    }

                    updateAnswer(id, JSON.stringify(newValue));
                };

                const handleCountryToggleClick = (countryCode) => {
                    const countryId = `COUNTRY-${countryCode}`;
                    const newValue = [...statesCountriesValue];
                    const index = newValue.indexOf(countryId);

                    if (index > -1) {
                        newValue.splice(index, 1);
                    } else {
                        newValue.push(countryId);
                    }

                    updateAnswer(id, JSON.stringify(newValue));
                };

                const usStatesDropdownId = `${id}-us-states`;
                const countriesDropdownId = `${id}-countries`;
                const isUsStatesOpen = openGeoDropdowns[usStatesDropdownId] || false;
                const isCountriesOpen = openGeoDropdowns[countriesDropdownId] || false;

                const getSelectedStatesText = () => {
                    const selectedStates = statesCountriesValue
                        .filter(val => val.startsWith('US-'))
                        .map(val => {
                            const stateCode = val.replace('US-', '');
                            const state = usStates.find(s => s.isoCode === stateCode);
                            return state?.name || stateCode;
                        });

                    if (selectedStates.length === 0) return 'Select US States';
                    if (selectedStates.length === 1) return selectedStates[0];
                    return `${selectedStates.length} states selected`;
                };

                const getSelectedCountriesText = () => {
                    const selectedCountries = statesCountriesValue
                        .filter(val => val.startsWith('COUNTRY-'))
                        .map(val => {
                            const countryCode = val.replace('COUNTRY-', '');
                            const country = internationalCountries.find(c => c.isoCode === countryCode);
                            return country?.name || countryCode;
                        });

                    if (selectedCountries.length === 0) return 'Select International Countries';
                    if (selectedCountries.length === 1) return selectedCountries[0];
                    return `${selectedCountries.length} countries selected`;
                };

                return (
                    <div className="question-group">
                        <div className="geographic-selector">
                            {/* US States Section */}
                            <div className="geo-section">
                                <label className="geo-label">US States</label>
                                <div
                                    className="custom-dropdown"
                                    ref={(el) => {
                                        if (el) geoDropdownRefs.current[usStatesDropdownId] = el;
                                    }}
                                >
                                    <div
                                        className="dropdown-trigger"
                                        onClick={() => setOpenGeoDropdowns(prev => ({
                                            ...prev,
                                            [usStatesDropdownId]: !prev[usStatesDropdownId]
                                        }))}
                                    >
                                        <span>{getSelectedStatesText()}</span>
                                        <span className="dropdown-arrow">{isUsStatesOpen ? '▲' : '▼'}</span>
                                    </div>
                                    {isUsStatesOpen && (
                                        <div className="dropdown-menu">
                                            <div className="states-grid">
                                                {usStates.map((state) => (
                                                    <label key={state.isoCode} className="checkbox-option">
                                                        <input
                                                            type="checkbox"
                                                            checked={isStateSelected(state.isoCode)}
                                                            onChange={() => handleStateToggleClick(state.isoCode)}
                                                        />
                                                        <span className="checkbox-text">{state.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* International Countries Section */}
                            <div className="geo-section">
                                <label className="geo-label">International Countries</label>
                                <div
                                    className="custom-dropdown"
                                    ref={(el) => {
                                        if (el) geoDropdownRefs.current[countriesDropdownId] = el;
                                    }}
                                >
                                    <div
                                        className="dropdown-trigger"
                                        onClick={() => setOpenGeoDropdowns(prev => ({
                                            ...prev,
                                            [countriesDropdownId]: !prev[countriesDropdownId]
                                        }))}
                                    >
                                        <span>{getSelectedCountriesText()}</span>
                                        <span className="dropdown-arrow">{isCountriesOpen ? '▲' : '▼'}</span>
                                    </div>
                                    {isCountriesOpen && (
                                        <div className="dropdown-menu">
                                            <div className="states-grid">
                                                {internationalCountries.map((country) => (
                                                    <label key={country.isoCode} className="checkbox-option">
                                                        <input
                                                            type="checkbox"
                                                            checked={isCountrySelected(country.isoCode)}
                                                            onChange={() => handleCountryToggleClick(country.isoCode)}
                                                        />
                                                        <span className="checkbox-text">{country.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="question-group">
                        <div className="error-state">
                            <div className="error-title">Unsupported Question Type</div>
                            <div>Question type "{question_type}" is not supported.</div>
                        </div>
                    </div>
                );
        }
    };

    if (loading) {
        return (
            <div className="dynamic-form">
                <LoadingSpinner message="Loading form configuration..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="dynamic-form">
                <div className="form-container">
                    <div className="error-state">
                        <div className="error-title">Error Loading Form</div>
                        <p>{error}</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => window.location.reload()}
                            style={{ marginTop: '16px' }}
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!formConfig) {
        return (
            <div className="dynamic-form">
                <div className="form-container">
                    <div className="no-config-state">
                        <div className="error-title">No Form Available</div>
                        <p>No form configuration found for this entity type.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dynamic-form">
            <div className="edit-profile-container">
                <div className="edit-profile-header">
                    <p>{formConfig.form_name}</p>
                </div>

                {/* Progressive Save Info Message */}
                <div style={{
                    backgroundColor: '#f0f7ff',
                    border: '1px solid #d0e7ff',
                    borderRadius: '8px',
                    padding: '16px 20px',
                    marginBottom: '24px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: '#1a1a1a',
                    textAlign: 'left'
                }}>
                    You can save your work as you go. Scroll to the bottom and click <strong>Save</strong> at any time, then return when you're ready to complete your profile. Your status will show as <strong>Incomplete</strong> until all required fields and drop-downs are filled in.
                </div>

                <form onSubmit={handleSubmit} className="form-container">
                    {formConfig.sections.map((section, sectionIndex) => (
                        <div key={section.id || sectionIndex} className="section-card">
                            <div className="section-header">
                                <h2 className="section-title">{section.section_name}</h2>
                            </div>
                            <div className="section-content">
                                {section.questions.map((question, questionIndex) => (
                                    <div key={question.id || questionIndex} className="question-group">
                                        <label className="question-label">
                                            {question.question_text}
                                            {question.is_required && <span className="required">*</span>}
                                        </label>
                                        {question.validation_rules?.placeholder && (
                                            <div className="question-description">
                                                {question.validation_rules.placeholder}
                                            </div>
                                        )}
                                        {renderQuestion(question)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onCancel}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Profile'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DynamicFormRenderer;
